import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Upload, ArrowUpCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

const GENRE_OPTIONS = [
  'Fantasy', 'Science Fiction', 'Romance', 'Mystery',
  'Thriller', 'Horror', 'Historical Fiction', 'Literary Fiction',
  'Young Adult', 'Children\'s', 'Biography', 'Non-fiction'
];

type Identity = {
  id: string;
  username: string;
  avatar_url: string | null;
};

export default function NewIdea() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [customTags, setCustomTags] = useState('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentIdentity, setCurrentIdentity] = useState<Identity | null>(null);
  const [userRole, setUserRole] = useState<'reader' | 'author' | 'admin' | null>(null);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserRole();
      fetchCurrentIdentity();
    }
  }, [user]);

  const fetchUserRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user!.id)
        .single();
      
      setUserRole(profile?.role || null);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchCurrentIdentity = async () => {
    if (!user) return;

    const currentIdentityId = localStorage.getItem('current_identity_id');
    
    if (!currentIdentityId) {
      // Use main profile if no pen name is selected
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('id', user.id)
        .single();
      
      if (profile) {
        setCurrentIdentity(profile);
      }
      return;
    }

    // Check if it's a pen name
    const { data: penName } = await supabase
      .from('pen_names')
      .select('id, username, avatar_url')
      .eq('id', currentIdentityId)
      .single();

    if (penName) {
      setCurrentIdentity(penName);
    } else {
      // Fallback to main profile if pen name not found
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('id', user.id)
        .single();
      
      if (profile) {
        setCurrentIdentity(profile);
      }
    }
  };

  const handleUpgradeToAuthor = async () => {
    if (!user) return;

    try {
      setUpgrading(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          role: 'author',
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setUserRole('author');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upgrade account');
    } finally {
      setUpgrading(false);
    }
  };

  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : prev.length < 3
        ? [...prev, genre]
        : prev
    );
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !currentIdentity || userRole === 'reader') return;
    
    try {
      setLoading(true);
      setError(null);

      let coverImageUrl = null;
      if (coverImage) {
        const fileExt = coverImage.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('covers')
          .upload(fileName, coverImage);

        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('covers')
          .getPublicUrl(fileName);
          
        coverImageUrl = publicUrl;
      }

      // Process custom tags
      const processedTags = customTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Insert the book idea with identity information
      const { error: ideaError } = await supabase
        .from('book_ideas')
        .insert({
          title,
          description,
          genre: selectedGenres,
          cover_image: coverImageUrl,
          custom_tags: processedTags,
          author_id: user.id,
          identity_id: currentIdentity.id,
          identity_username: currentIdentity.username,
          identity_avatar_url: currentIdentity.avatar_url
        });

      if (ideaError) throw ideaError;

      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create book idea');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {userRole === 'reader' && (
        <div className="mb-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg shadow-md p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Upgrade to Author</h3>
              <p className="text-indigo-100 mb-4">
                To share your book ideas, you'll need to upgrade your account to an author account. 
                This will give you access to all author features including creating book ideas, 
                managing characters, and engaging with readers.
              </p>
            </div>
            <ArrowUpCircle className="w-8 h-8 text-indigo-200" />
          </div>
          <button
            onClick={handleUpgradeToAuthor}
            disabled={upgrading}
            className="px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
          >
            {upgrading ? 'Upgrading...' : 'Upgrade Now'}
          </button>
        </div>
      )}

      <div className={`bg-white rounded-lg shadow-md p-6 md:p-8 ${userRole === 'reader' ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Share Your Book Idea</h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={6}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={customTags}
              onChange={(e) => setCustomTags(e.target.value)}
              placeholder="e.g., magic system, dragons, epic battles, enemies to lovers, chosen one"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="mt-1 text-sm text-gray-500">
              Add any relevant tags including story elements, tropes, themes, etc.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Genres (select up to 3)
            </label>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => handleGenreToggle(genre)}
                  disabled={selectedGenres.length >= 3 && !selectedGenres.includes(genre)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedGenres.includes(genre)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${
                    selectedGenres.length >= 3 && !selectedGenres.includes(genre)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Image
            </label>
            <div className="flex items-start gap-4">
              {coverPreview && (
                <img
                  src={coverPreview}
                  alt="Cover preview"
                  className="w-32 h-32 object-cover rounded-lg"
                />
              )}
              <label className="flex-1">
                <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-center">
                  <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  Choose cover
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleCoverChange}
                />
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !title || !description || selectedGenres.length === 0 || !currentIdentity || userRole === 'reader'}
            className="w-full bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Share Book Idea'}
          </button>
        </form>
      </div>
    </div>
  );
}